import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import {
  type ShopShippingRule,
  type ShopShippingRuleInput,
  listShippingRules,
  createShippingRule,
  updateShippingRule,
  deleteShippingRule,
} from '@/services/shopAdminApi';
import { Plus, Trash2, Save, X, AlertCircle, CheckCircle2, Loader2, Truck, MapPin } from 'lucide-react';

const EMPTY: ShopShippingRuleInput = {
  name: '',
  zone: '',
  suburb_pattern: '',
  method: 'flat_rate',
  base_rate: 0,
  free_threshold: null,
  weight_rate_per_kg: null,
  min_days: null,
  max_days: null,
  is_active: true,
  priority: 0,
};

const METHOD_OPTIONS = [
  { value: 'flat_rate', label: 'Flat rate' },
  { value: 'courier', label: 'Courier' },
  { value: 'local_pickup', label: 'Local pickup' },
  { value: 'express', label: 'Express' },
];

export function ShippingRulesTab() {
  const [items, setItems] = useState<ShopShippingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ShopShippingRuleInput>(EMPTY);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await listShippingRules();
      setItems(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load shipping rules');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setEditingId('new');
    setForm(EMPTY);
  }

  function startEdit(r: ShopShippingRule) {
    setEditingId(r.id);
    setForm({
      name: r.name,
      zone: r.zone || '',
      suburb_pattern: r.suburb_pattern || '',
      method: r.method,
      base_rate: r.base_rate,
      free_threshold: r.free_threshold,
      weight_rate_per_kg: r.weight_rate_per_kg,
      min_days: r.min_days,
      max_days: r.max_days,
      is_active: r.is_active,
      priority: r.priority,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY);
  }

  function update<K extends keyof ShopShippingRuleInput>(key: K, value: ShopShippingRuleInput[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      if (editingId === 'new') {
        await createShippingRule(form);
        setMessage({ type: 'success', text: 'Shipping rule created.' });
      } else if (editingId) {
        await updateShippingRule(editingId, form);
        setMessage({ type: 'success', text: 'Shipping rule updated.' });
      }
      setEditingId(null);
      setForm(EMPTY);
      await load();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this shipping rule?')) return;
    try {
      await deleteShippingRule(id);
      await load();
      setMessage({ type: 'success', text: 'Deleted.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Delete failed.' });
    }
  }

  if (loading) return <div className="p-8 text-center text-[#6B7B6E]">Loading shipping rules…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1A2E12]">Shipping Rules</h2>
        <button
          onClick={startCreate}
          disabled={editingId === 'new'}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add Rule
        </button>
      </div>

      {message && (
        <div className={cn(
          'flex items-start gap-2 rounded-lg px-4 py-3 text-sm',
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        )}>
          {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
          {message.text}
        </div>
      )}

      {editingId === 'new' && (
        <RuleForm form={form} update={update} saving={saving} onSave={handleSave} onCancel={cancelEdit} />
      )}

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-[#6B7B6E]">No shipping rules yet.</p>}
        {items.map(r => (
          <div key={r.id} className="bg-white rounded-xl border border-[#D9B38C]/20 overflow-hidden">
            {editingId === r.id ? (
              <RuleForm form={form} update={update} saving={saving} onSave={handleSave} onCancel={cancelEdit} />
            ) : (
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#567D38]/10 text-[#567D38] flex items-center justify-center shrink-0">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1A2E12]">{r.name}</p>
                    <p className="text-sm text-[#6B7B6E]">
                      Base R{r.base_rate}
                      {r.free_threshold ? ` · free over R${r.free_threshold}` : ''}
                      {r.weight_rate_per_kg ? ` · R${r.weight_rate_per_kg}/kg` : ''}
                      {r.min_days || r.max_days ? ` · ${[r.min_days, r.max_days].filter(Boolean).join('–')} days` : ''}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-[#6B7B6E]">
                      <span className="px-2 py-1 bg-[#F7F5EE] rounded">{r.method}</span>
                      {(r.zone || r.suburb_pattern) && (
                        <span className="px-2 py-1 bg-[#F7F5EE] rounded flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {[r.zone, r.suburb_pattern].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-[#F7F5EE] rounded">priority {r.priority}</span>
                      <span className={cn('px-2 py-1 rounded', r.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>{r.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEdit(r)} className="p-2 text-[#6B7B6E] hover:text-[#1A2E12] hover:bg-[#F7F5EE] rounded-lg"><Save className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(r.id)} className="p-2 text-[#6B7B6E] hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RuleForm({
  form,
  update,
  saving,
  onSave,
  onCancel,
}: {
  form: ShopShippingRuleInput;
  update: <K extends keyof ShopShippingRuleInput>(key: K, value: ShopShippingRuleInput[K]) => void;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="bg-[#F7F5EE] rounded-xl p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Rule name" value={form.name} onChange={v => update('name', v)} />
        <Select label="Method" value={form.method} onChange={v => update('method', v)} options={METHOD_OPTIONS} />
        <Input label="Zone" value={form.zone || ''} onChange={v => update('zone', v)} />
        <Input label="Suburb pattern (comma-separated)" value={form.suburb_pattern || ''} onChange={v => update('suburb_pattern', v)} />
        <Input label="Base rate" type="number" value={String(form.base_rate)} onChange={v => update('base_rate', parseFloat(v || '0'))} />
        <Input label="Free threshold" type="number" value={form.free_threshold != null ? String(form.free_threshold) : ''} onChange={v => update('free_threshold', v ? parseFloat(v) : null)} />
        <Input label="Weight rate per kg" type="number" value={form.weight_rate_per_kg != null ? String(form.weight_rate_per_kg) : ''} onChange={v => update('weight_rate_per_kg', v ? parseFloat(v) : null)} />
        <Input label="Min days" type="number" value={form.min_days != null ? String(form.min_days) : ''} onChange={v => update('min_days', v ? parseInt(v, 10) : null)} />
        <Input label="Max days" type="number" value={form.max_days != null ? String(form.max_days) : ''} onChange={v => update('max_days', v ? parseInt(v, 10) : null)} />
        <Input label="Priority" type="number" value={String(form.priority)} onChange={v => update('priority', parseInt(v || '0', 10))} />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[#1A2E12]">
            <input type="checkbox" checked={form.is_active} onChange={e => update('is_active', e.target.checked)} />
            Active
          </label>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-[#6B7B6E] hover:bg-white rounded-lg"><X className="w-4 h-4 inline mr-1" /> Cancel</button>
        <button onClick={onSave} disabled={saving} className="px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <Save className="w-4 h-4 inline mr-1" />} Save
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630]"
      />
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630] bg-white"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
